import { UserSurveyService } from "@domain/user/user-survey-service";
import { observable } from "micro-observables";
import { Survey } from "@domain/user/user-survey";
import { isNotNil } from "@utils/assert";

interface SurveyDialogDisplayContext {
	visitedPage: string | undefined;
	pageVisitCount: number;
	displayDate: Date | undefined;
}

export class UserSurveyManager {
	private static readonly MIN_PAGES_VISITED = 6;
	private static readonly STOP_PERIOD_IN_DAYS = 1;

	private surveyDialogDisplayContext: SurveyDialogDisplayContext = {
		visitedPage: undefined,
		pageVisitCount: 0,
		displayDate: undefined,
	};

	private readonly _isSurveyCompleted = observable<boolean>(false);
	readonly isSurveyCompleted = this._isSurveyCompleted.readOnly();

	constructor(private readonly userSurveyService: UserSurveyService) {}

	async processPage(url: string, showDialogCallback: (survey: Survey) => Promise<void>): Promise<void> {
		// displayDate
		if (
			isNotNil(this.surveyDialogDisplayContext.displayDate) &&
			new Date() < this.surveyDialogDisplayContext.displayDate
		) {
			return;
		}

		// visitedPage
		if (url === this.surveyDialogDisplayContext.visitedPage) {
			return;
		}
		this.surveyDialogDisplayContext.visitedPage = url;

		// pageVisitCount
		if (++this.surveyDialogDisplayContext.pageVisitCount < UserSurveyManager.MIN_PAGES_VISITED) {
			return;
		}

		// reset context for next time
		this.surveyDialogDisplayContext = {
			visitedPage: undefined,
			pageVisitCount: 0,
			displayDate: UserSurveyManager.getSurveyDialogDisplayDate(),
		};
		this._isSurveyCompleted.update(() => false);

		const surveys = await this.listSurveys();
		if (surveys.length > 0) {
			await showDialogCallback(surveys[0]);
		}
	}

	async listSurveys(): Promise<Survey[]> {
		return await this.userSurveyService.listSurveys();
	}

	async completeSurvey(id: string): Promise<void> {
		await this.userSurveyService.completeSurvey(id);
		this._isSurveyCompleted.update(() => true);
	}

	async dismissSurvey(id: string): Promise<void> {
		await this.userSurveyService.dismissSurvey(id);
	}

	private static getSurveyDialogDisplayDate(): Date {
		const date = new Date();
		date.setDate(date.getDate() + UserSurveyManager.STOP_PERIOD_IN_DAYS);
		return date;
	}
}
